import { Coins } from "@tamagui/lucide-icons";
import Big from "big.js";
import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import { getLanguage } from "../../core/utils";
import type UserModel from "../../users/UserModel";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import BaseModuleModel, { type ModuleModelData } from "../core/BaseModuleModel";
import ExpenseItemModule, {
  type ExpenseItemModuleData,
  type Split,
} from "./data/ExpenseItemModel";
import SettlementItemModule, {
  type SettlementItemModuleData,
} from "./data/SettlementItemModule";
import { ExpenseModuleBlockUI } from "./ui/ExpenseModuleBlockUI";
import { ExpenseModuleUI } from "./ui/ExpenseModuleUI";

export type ExpenseModuleSnapshot = {
  totalUnsettledDebts: number;
  totalExpenses: number;
};

export type ExpenseDebt = {
  debtorUser?: UserModel;
  creditorUser?: UserModel;
  amount: Big;
};

export interface ExpenseModuleModelData
  extends ModuleModelData<ExpenseModuleSnapshot> {
  currencyCode: string;
}
export default class ExpenseModuleModel extends BaseModuleModel<
  ExpenseModuleSnapshot,
  ExpenseModuleModelData
> {
  constructor(stores: Stores, api: Api, data: ExpenseModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      currencyCode: data.currencyCode ?? "USD",
    });

    makeObservable(this);
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = {
      totalUnsettledDebts: this.remainingDebts.length,
      totalExpenses: this.expenses.length,
    };
  }

  @computed get expenses(): ExpenseItemModule[] {
    return this.dataItems.filter((item): item is ExpenseItemModule =>
      ExpenseItemModule.is(item),
    );
  }

  @computed get settlements(): SettlementItemModule[] {
    return this.dataItems.filter((item): item is SettlementItemModule =>
      SettlementItemModule.is(item),
    );
  }

  @computed get expensesAndSettlements(): (
    | SettlementItemModule
    | ExpenseItemModule
  )[] {
    return [...this.settlements, ...this.expenses].sort((a, b) => {
      return a.data.timestamp?.toMillis() > b.data.timestamp?.toMillis()
        ? -1
        : 1;
    });
  }

  @action async addExpense(
    data: Partial<ExpenseItemModuleData> & {
      title: string;
      amount: Big;
      paidByUserId: string;
      splits: Split[];
    },
  ) {
    if (data.amount.lte(0)) {
      throw new Error("Expense amount cannot be zero or negative.");
    }

    const totalSplitsAmount = data.splits
      .reduce((total, split) => total.plus(split.amount), new Big(0))
      .toNumber();

    if (!data.amount.eq(totalSplitsAmount)) {
      throw new Error("Splits do not sum up to the total amount.");
    }

    const expense = await this.addNewData<ExpenseItemModuleData>(
      ModuleDataType.Expense,
      {
        ...data,
        amount: data.amount.toNumber() as unknown as Big,
        splits: data.splits.map((split) => ({
          ...split,
          amount: split.amount.toNumber() as unknown as Big,
        })),
      },
      TKey.ActivityExpensesExpenseAdded,
      {
        title: data.title,
        amount: this.currencyFormatter.format(data.amount.toNumber()),
      },
    );
    await this.updateSnapshotWithoutSave();
    return expense;
  }

  @action async addSettlement(
    data: Partial<SettlementItemModuleData> & {
      debtorUserId: string;
      creditorUserId: string;
      amountPaid: number;
    },
  ) {
    if (Number.isNaN(data.amountPaid) || data.amountPaid < 0) {
      throw new Error("Settlement amount cannot be negative.");
    }

    const creditorUser = this.stores.usersStore.byId.get(data.creditorUserId);
    const debtorUser = this.stores.usersStore.byId.get(data.debtorUserId);

    const settlement = await this.addNewData<SettlementItemModule>(
      ModuleDataType.Settlement,
      data,
      TKey.ActivityExpensesSettlementAdded,
      {
        debtorName: debtorUser?.data.displayName ?? "Unknown",
        creditorName: creditorUser?.data.displayName ?? "Unknown",
        amount: this.currencyFormatter.format(data.amountPaid),
      },
    );
    await this.updateSnapshotWithoutSave();

    return settlement;
  }

  @computed
  get remainingDebts(): ExpenseDebt[] {
    const users = this.plan.users;
    const debts = new Map<string, Big>(); // Map of user IDs to their net balance (positive for credit, negative for debt)

    // Compile debts and credits from expenses
    this.expenses.forEach((expense) => {
      const amountPaidBy = expense.data.paidByUserId;
      const paidAmount = expense.data.amount;
      debts.set(
        amountPaidBy,
        (debts.get(amountPaidBy) || new Big(0)).plus(paidAmount),
      );

      expense.data.splits.forEach((split) => {
        const oweUser = split.userId;
        const oweAmount = split.amount;
        debts.set(oweUser, (debts.get(oweUser) || new Big(0)).minus(oweAmount));
      });
    });

    // Apply settlements to the initial debt/credit balances
    this.settlements.forEach((settlement) => {
      const { debtorUserId, creditorUserId, amountPaid } = settlement.data;
      debts.set(
        debtorUserId,
        (debts.get(debtorUserId) || new Big(0)).plus(amountPaid),
      );
      debts.set(
        creditorUserId,
        (debts.get(creditorUserId) || new Big(0)).minus(amountPaid),
      );
    });

    // Convert the balance map into sorted creditor and debtor lists
    const creditors: { id: string; amount: Big }[] = [];
    const debtors: { id: string; amount: Big }[] = [];

    debts.forEach((amount, id) => {
      if (amount.gt(0)) {
        creditors.push({ id, amount });
      } else if (amount.lt(0)) {
        debtors.push({ id, amount: amount.neg() });
      }
    });

    creditors.sort((a, b) => b.amount.minus(a.amount).toNumber());
    debtors.sort((a, b) => b.amount.minus(a.amount).toNumber());

    const settlements: {
      debtorUser?: UserModel;
      creditorUser?: UserModel;
      amount: Big;
    }[] = [];

    // Greedily match creditors and debtors
    let i = 0;
    let j = 0;
    while (i < creditors.length && j < debtors.length) {
      const creditor = creditors[i];
      const debtor = debtors[j];
      const settleAmount = Big(
        Math.min(creditor.amount.toNumber(), debtor.amount.toNumber()),
      );

      if (settleAmount.gte(0.01)) {
        const debtorUser = users.find((user) => user.uid === debtor.id);
        const creditorUser = users.find((user) => user.uid === creditor.id);

        settlements.push({
          debtorUser,
          creditorUser,
          amount: settleAmount,
        });

        creditor.amount = creditor.amount.minus(settleAmount);
        debtor.amount = debtor.amount.minus(settleAmount);
      }

      if (creditor.amount.lt(0.01)) i++;
      if (debtor.amount.lt(0.01)) j++;
    }

    return settlements;
  }

  @computed
  get currencyFormatter() {
    return new Intl.NumberFormat(getLanguage(), {
      style: "currency",
      currency: this.data.currencyCode ?? "USD",
    });
  }

  render() {
    return <ExpenseModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <ExpenseModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <Coins size={size} />;
  }
}
